import { secp256k1 } from "@noble/curves/secp256k1.js";
import { keccak_256 } from "@noble/hashes/sha3.js";
import { SignDoc, TxRaw } from "cosmjs-types/cosmos/tx/v1beta1/tx.js";

import {
  assertUnchangedSignDoc,
  queryAccount,
  queryBalance,
  waitForInclusion,
} from "../probes/keplr-send/tx.mjs";
import { CHAIN } from "./config.mjs";

export { queryAccount, queryBalance, waitForInclusion };

function fromBase64(value) {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function toBase64(bytes) {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function hex(bytes) {
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("").toUpperCase();
}

export function verifyKeplrSignature(signDoc, signatureBase64, publicKey) {
  const signature = fromBase64(signatureBase64);
  if (signature.length !== 64) throw new Error("Keplr signature is not compact 64-byte");
  return secp256k1.verify(
    signature,
    keccak_256(SignDoc.encode(signDoc).finish()),
    publicKey,
    { format: "compact", lowS: true, prehash: false },
  );
}

export async function signVerified({
  keplr,
  address,
  publicKey,
  signDoc,
}) {
  const response = await keplr.signDirect(
    CHAIN.chainId,
    address,
    signDoc,
    { preferNoSetFee: true, preferNoSetMemo: true },
  );
  assertUnchangedSignDoc(signDoc, response.signed);
  if (!verifyKeplrSignature(response.signed, response.signature.signature, publicKey)) {
    throw new Error("Keplr signature failed local ethsecp256k1 verification");
  }
  return TxRaw.encode(TxRaw.fromPartial({
    bodyBytes: response.signed.bodyBytes,
    authInfoBytes: response.signed.authInfoBytes,
    signatures: [fromBase64(response.signature.signature)],
  })).finish();
}

export async function broadcastVerified(fetchFn, txBytes) {
  const response = await fetchFn(`${CHAIN.rest}/cosmos/tx/v1beta1/txs`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      tx_bytes: toBase64(txBytes),
      mode: "BROADCAST_MODE_SYNC",
    }),
  });
  if (!response.ok) throw new Error(`broadcast failed (HTTP ${response.status})`);
  const result = (await response.json())?.tx_response;
  if (!result?.txhash) throw new Error("broadcast returned no transaction hash");
  if (Number(result.code) !== 0) {
    throw new Error(`broadcast rejected transaction (code ${result.code}): ${result.raw_log}`);
  }
  return String(result.txhash).toUpperCase();
}

export async function signAndBroadcastVerified(options) {
  const txBytes = await signVerified(options);
  try {
    return await broadcastVerified(options.fetchFn, txBytes);
  } catch (error) {
    if (!(error instanceof TypeError)) throw error;
    if (typeof options.keplr.sendTx !== "function") {
      throw new Error("REST broadcast was blocked and Keplr sendTx is unavailable");
    }
    return hex(await options.keplr.sendTx(CHAIN.chainId, txBytes, "sync"));
  }
}

export function txHashBytes(txBytes) {
  return hex(keccak_256(txBytes));
}
